import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Grid3x3,
  List,
  Mail,
  Briefcase,
  Building2,
  TrendingUp,
  Calendar,
  Users,
  Settings2,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
} from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PaginationControls } from '@/components/pagination-controls';
import { usePagination } from '@/hooks/use-pagination';
import { paginateArray } from '@/types/pagination';
import { ColumnConfiguratorDrawer, type ColumnDefinition } from '@/components/column-configurator-drawer';
import { useColumnPreferences } from '@/hooks/use-column-preferences';
import type { Employee as ApiEmployee, ResourceAllocation } from '@/types';
import { fetchEmployees, fetchResourceAllocations } from '@/lib/data-service';
import { useDataMode } from '@/contexts/DataModeContext';

interface ProjectAllocation {
  projectId: number;
  projectName: string;
  projectCode: string;
  allocation: number;
  startDate: string;
  endDate: string;
  role: string;
}

interface RatesResource {
  id: string;
  name: string;
  email: string;
  role: string;
  department: string;
  image: string;
  type: string;
  status: 'Active' | 'Inactive';
  grade: string;
  costRate: number;
  billRate: number;
  projectAllocations: ProjectAllocation[];
}

function mapApiEmployee(
  emp: ApiEmployee,
  allocations: ResourceAllocation[]
): RatesResource {
  const empAllocations = allocations.filter(a => a.employeeId === emp.id);
  return {
    id: String(emp.id),
    name: emp.name,
    email: emp.email,
    role: emp.role,
    department: emp.department,
    image: emp.photo || '',
    type: 'Person',
    status: emp.isAvailable ? 'Active' : 'Inactive',
    grade: '',
    costRate: emp.costRate,
    billRate: emp.billRate,
    projectAllocations: empAllocations.map(a => ({
      projectId: a.projectId,
      projectName: a.projectName || 'Project ' + a.projectId,
      projectCode: '',
      allocation: a.allocationPercent || 0,
      startDate: a.startDate,
      endDate: a.endDate,
      role: a.role,
    })),
  };
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const calculateMargin = (costRate: number, billRate: number) => {
  if (billRate === 0) return 0;
  return Math.round((billRate - costRate) / billRate * 100);
};

const getMarginColor = (margin: number) => {
  if (margin >= 60) return 'bg-green-100 text-green-800 border-green-200';
  if (margin >= 40) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
  if (margin > 0) return 'bg-orange-100 text-orange-800 border-orange-200';
  return 'bg-gray-100 text-gray-800 border-gray-200';
};

const RateDetailSheet: React.FC<{
  employee: RatesResource | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  navigate: (path: string) => void;
}> = ({ employee, open, onOpenChange, navigate }) => {
  if (!employee) return null;

  const margin = calculateMargin(employee.costRate, employee.billRate);
  const dailyCost = employee.costRate * 8;
  const dailyBill = employee.billRate * 8;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-3xl p-0 flex flex-col">
        <div className="p-6 border-b">
          <div className="flex items-start gap-4">
            <Avatar className="h-16 w-16 rounded-lg">
              <AvatarImage src={employee.image} alt={employee.name} />
              <AvatarFallback className="text-xl">
                {employee.name
                  .split(' ')
                  .map((n) => n[0])
                  .join('')}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-2xl font-bold">{employee.name}</h2>
                  <p className="text-gray-600 mt-1">{employee.role}</p>
                </div>
                <Badge
                  className={employee.status === 'Active' ? 'bg-green-100 text-green-800 border-green-200' : 'bg-gray-100 text-gray-800 border-gray-200'}
                  variant="outline"
                >
                  {employee.status}
                </Badge>
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 bg-gray-50 border-b">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Profile</h3>
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-3">
              <h4 className="text-xs font-medium text-gray-500 uppercase">Contact</h4>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <span className="text-teal-600">{employee.email}</span>
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <h4 className="text-xs font-medium text-gray-500 uppercase">Work</h4>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Briefcase className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600">Role:</span>
                  <span className="ml-auto font-medium">{employee.role}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Building2 className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600">Department:</span>
                  <span className="ml-auto font-medium">{employee.department}</span>
                </div>
                {employee.grade && (
                  <div className="flex items-center gap-2 text-sm">
                    <TrendingUp className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">Grade:</span>
                    <Badge variant="outline" className="ml-auto">
                      {employee.grade}
                    </Badge>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-hidden">
          <Tabs defaultValue="rates" className="h-full flex flex-col">
            <TabsList className="w-full justify-start rounded-none border-b bg-transparent px-6">
              <TabsTrigger value="rates" className="rounded-none border-b-2 border-transparent data-[state=active]:border-teal-600 data-[state=active]:bg-transparent">
                Rates
              </TabsTrigger>
              <TabsTrigger value="projects" className="rounded-none border-b-2 border-transparent data-[state=active]:border-teal-600 data-[state=active]:bg-transparent">
                Projects
              </TabsTrigger>
            </TabsList>

            <TabsContent value="rates" className="flex-1 overflow-y-auto p-6 mt-0">
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-4">Bill Rates</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-teal-50 p-4 rounded-lg border border-teal-100">
                      <p className="text-sm text-gray-600 mb-1">Hour Rate</p>
                      <p className="text-2xl font-bold text-teal-700">
                        {formatCurrency(employee.billRate)}
                      </p>
                    </div>
                    <div className="bg-teal-50 p-4 rounded-lg border border-teal-100">
                      <p className="text-sm text-gray-600 mb-1">Day Rate</p>
                      <p className="text-2xl font-bold text-teal-700">
                        {formatCurrency(dailyBill)}
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-lg font-semibold mb-4">Cost Rates</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <p className="text-sm text-gray-600 mb-1">Hour Rate</p>
                      <p className="text-2xl font-bold text-gray-700">
                        {formatCurrency(employee.costRate)}
                      </p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <p className="text-sm text-gray-600 mb-1">Day Rate</p>
                      <p className="text-2xl font-bold text-gray-700">
                        {formatCurrency(dailyCost)}
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-lg font-semibold mb-4">Margin</h3>
                  <div className="bg-white p-4 rounded-lg border">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Margin</span>
                      <Badge className={getMarginColor(margin)} variant="outline">
                        {margin}%
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="projects" className="flex-1 overflow-y-auto p-6 mt-0">
              <h3 className="text-lg font-semibold mb-4">Project Allocations</h3>
              {employee.projectAllocations.length > 0 ? (
                <div className="space-y-3">
                  {employee.projectAllocations.map((project, index) => (
                    <Card key={index} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <button
                              onClick={() => navigate('/projects/' + project.projectId)}
                              className="text-left hover:underline focus:outline-none focus:underline"
                            >
                              <p className="font-semibold text-lg text-teal-600 hover:text-teal-700">
                                {project.projectName}
                              </p>
                              <p className="text-sm text-teal-500 hover:text-teal-600">{project.projectCode}</p>
                            </button>
                          </div>
                          <Badge className="bg-teal-100 text-teal-700 border-teal-200">
                            {project.allocation}% Allocated
                          </Badge>
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm">
                            <Briefcase className="h-4 w-4 text-gray-400" />
                            <span className="text-gray-600">Role:</span>
                            <span className="font-medium">{project.role}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            <span>
                              {new Date(project.startDate).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })}
                              {' - '}
                              {new Date(project.endDate).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No current project allocations</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </SheetContent>
    </Sheet>
  );
};

const DEFAULT_RATES_COLUMNS: ColumnDefinition[] = [
  {
    id: 'name',
    name: 'Name',
    description: 'Employee name with avatar',
    category: 'Resource',
    visible: true,
    locked: true,
    order: 0,
  },
  {
    id: 'role',
    name: 'Role',
    description: 'Job role',
    category: 'Resource',
    visible: true,
    order: 1,
  },
  {
    id: 'department',
    name: 'Department',
    description: 'Department',
    category: 'Resource',
    visible: true,
    order: 2,
  },
  {
    id: 'email',
    name: 'Email',
    description: 'Email address',
    category: 'Resource',
    visible: false,
    order: 3,
  },
  {
    id: 'status',
    name: 'Status',
    description: 'Active or Inactive',
    category: 'Status',
    visible: false,
    order: 4,
  },
  {
    id: 'cost-rate-hour',
    name: 'Cost Rate (Hour)',
    description: 'Hourly cost rate',
    category: 'Financial',
    visible: true,
    order: 5,
    align: 'right',
  },
  {
    id: 'cost-rate-day',
    name: 'Cost Rate (Day)',
    description: 'Daily cost rate',
    category: 'Financial',
    visible: true,
    order: 6,
    align: 'right',
  },
  {
    id: 'bill-rate-hour',
    name: 'Bill Rate (Hour)',
    description: 'Hourly bill rate',
    category: 'Financial',
    visible: true,
    order: 7,
    align: 'right',
  },
  {
    id: 'bill-rate-day',
    name: 'Bill Rate (Day)',
    description: 'Daily bill rate',
    category: 'Financial',
    visible: true,
    order: 8,
    align: 'right',
  },
  {
    id: 'margin',
    name: 'Margin',
    description: 'Profit margin percentage',
    category: 'Financial',
    visible: true,
    order: 9,
    align: 'right',
  },
  {
    id: 'project-count',
    name: 'Active Projects',
    description: 'Number of active projects',
    category: 'Performance',
    visible: false,
    order: 10,
    align: 'right',
  },
];

export function RolesRatesPage() {
  const navigate = useNavigate();
  const { mode } = useDataMode();
  const [searchQuery, setSearchQuery] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedEmployee, setSelectedEmployee] = useState<RatesResource | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);
  const [employees, setEmployees] = useState<RatesResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadEmployees = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [apiEmployees, allocations] = await Promise.all([
        fetchEmployees(),
        fetchResourceAllocations().catch(() => [] as ResourceAllocation[]),
      ]);
      const mapped = (apiEmployees as ApiEmployee[]).map(emp =>
        mapApiEmployee(emp, allocations as ResourceAllocation[])
      );
      setEmployees(mapped);
    } catch (err) {
      console.error('Failed to load roles & rates data:', err);
      setError('Failed to load employee data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees, mode]);

  const { columns, updateColumns } = useColumnPreferences({
    storageKey: 'roles-rates-column-preferences',
    defaultColumns: DEFAULT_RATES_COLUMNS,
  });

  const {
    currentPage,
    pageSize,
    setPage,
    setPageSize,
    resetPagination,
  } = usePagination({ defaultPageSize: 25, persistInUrl: false });

  const departments = useMemo(
    () => ['all', ...Array.from(new Set(employees.map((e) => e.department).filter(Boolean)))],
    [employees]
  );
  const roles = useMemo(
    () => ['all', ...Array.from(new Set(employees.map((e) => e.role).filter(Boolean)))],
    [employees]
  );

  const filteredEmployees = useMemo(() => {
    return employees
      .filter((employee) => {
        const matchesSearch =
          employee.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          employee.role.toLowerCase().includes(searchQuery.toLowerCase()) ||
          employee.email.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesDepartment =
          departmentFilter === 'all' || employee.department === departmentFilter;
        const matchesRole = roleFilter === 'all' || employee.role === roleFilter;
        return matchesSearch && matchesDepartment && matchesRole;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [employees, searchQuery, departmentFilter, roleFilter]);

  useEffect(() => {
    resetPagination();
  }, [searchQuery, departmentFilter, roleFilter]);

  const paginatedResult = useMemo(() => {
    return paginateArray(filteredEmployees, currentPage, pageSize);
  }, [filteredEmployees, currentPage, pageSize]);

  const displayedEmployees = paginatedResult.data;
  const pagination = paginatedResult.pagination;

  const handleEmployeeClick = (employee: RatesResource) => {
    setSelectedEmployee(employee);
    setSheetOpen(true);
  };

  const visibleColumns = useMemo(() => {
    return columns
      .filter((col) => col.visible)
      .sort((a, b) => a.order - b.order);
  }, [columns]);

  const renderTableCell = (employee: RatesResource, columnId: string) => {
    const margin = calculateMargin(employee.costRate, employee.billRate);
    switch (columnId) {
      case 'name':
        return (
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10 rounded-md">
              <AvatarImage src={employee.image} alt={employee.name} />
              <AvatarFallback>
                {employee.name
                  .split(' ')
                  .map((n) => n[0])
                  .join('')}
              </AvatarFallback>
            </Avatar>
            <span className="font-medium">{employee.name}</span>
          </div>
        );
      case 'role':
        return employee.role;
      case 'department':
        return <Badge variant="outline">{employee.department}</Badge>;
      case 'email':
        return (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Mail className="h-4 w-4" />
            <span>{employee.email}</span>
          </div>
        );
      case 'status':
        return <Badge variant={employee.status === 'Active' ? 'default' : 'secondary'}>{employee.status}</Badge>;
      case 'cost-rate-hour':
        return formatCurrency(employee.costRate);
      case 'cost-rate-day':
        return formatCurrency(employee.costRate * 8);
      case 'bill-rate-hour':
        return <span className="text-green-600">{formatCurrency(employee.billRate)}</span>;
      case 'bill-rate-day':
        return <span className="text-green-600">{formatCurrency(employee.billRate * 8)}</span>;
      case 'margin':
        return (
          <Badge className={getMarginColor(margin)} variant="outline">
            {margin}%
          </Badge>
        );
      case 'project-count':
        return employee.projectAllocations.length;
      default:
        return null;
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Roles & Rates</h1>
        <p className="text-gray-600 mt-1">View and compare resource rates across your organization</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder="Search by name, role, or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
            <SelectTrigger className="w-[180px]">
              <Building2 className="h-4 w-4 mr-2" />
              <SelectValue placeholder="All Departments" />
            </SelectTrigger>
            <SelectContent>
              {departments.map((dept) => (
                <SelectItem key={dept} value={dept}>
                  {dept === 'all' ? 'All Departments' : dept}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-[180px]">
              <Briefcase className="h-4 w-4 mr-2" />
              <SelectValue placeholder="All Roles" />
            </SelectTrigger>
            <SelectContent>
              {roles.map((role) => (
                <SelectItem key={role} value={role}>
                  {role === 'all' ? 'All Roles' : role}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex gap-1 border rounded-md">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('grid')}
              className="rounded-r-none"
            >
              <Grid3x3 className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className="rounded-l-none"
            >
              <List className="h-4 w-4" />
            </Button>
          </div>

          {viewMode === 'list' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setColumnConfigOpen(true)}
              className="gap-2"
            >
              <Settings2 className="h-4 w-4" />
              Columns
            </Button>
          )}
        </div>
      </div>

      <div className="text-sm text-gray-600">
        Showing <span className="font-semibold">{pagination.startIndex + 1}</span> to{' '}
        <span className="font-semibold">{pagination.endIndex}</span> of{' '}
        <span className="font-semibold">{filteredEmployees.length}</span> resources
        {filteredEmployees.length !== employees.length && (
          <span className="text-gray-500"> (filtered from {employees.length} total)</span>
        )}
      </div>

      {loading && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-4" />
          <p className="text-gray-500">Loading resources...</p>
        </div>
      )}

      {error && !loading && (
        <div className="text-center py-12">
          <p className="text-red-500 mb-2">{error}</p>
          <Button variant="outline" onClick={loadEmployees}>Retry</Button>
        </div>
      )}

      {!loading && !error && displayedEmployees.length > 0 ? (
        viewMode === 'grid' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
            {displayedEmployees.map((employee) => {
              const margin = calculateMargin(employee.costRate, employee.billRate);
              return (
                <Card
                  key={employee.id}
                  className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
                  onClick={() => handleEmployeeClick(employee)}
                >
                  <div className="relative overflow-hidden h-48">
                    {employee.image ? (
                      <img
                        src={employee.image}
                        alt={employee.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          const target = e.currentTarget;
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent) {
                            const fallback = document.createElement('div');
                            fallback.className = 'w-full h-full bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center';
                            const initials = document.createElement('span');
                            initials.className = 'text-5xl font-semibold text-white';
                            initials.textContent = employee.name.split(' ').map((n) => n[0]).join('');
                            fallback.appendChild(initials);
                            parent.appendChild(fallback);
                          }
                        }}
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
                        <span className="text-5xl font-semibold text-white">
                          {employee.name.split(' ').map((n) => n[0]).join('')}
                        </span>
                      </div>
                    )}
                    <Badge
                      className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm"
                      variant="secondary"
                    >
                      {employee.role}
                    </Badge>
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-lg mb-1">{employee.name}</h3>
                    <p className="text-sm text-gray-600 mb-3">{employee.department}</p>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Bill Rate</span>
                        <span className="font-semibold text-teal-700">{formatCurrency(employee.billRate)}/hr</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Cost Rate</span>
                        <span className="font-medium text-gray-700">{formatCurrency(employee.costRate)}/hr</span>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t flex items-center justify-between">
                      <Badge variant="outline" className="text-xs">
                        {employee.department}
                      </Badge>
                      <Badge className={'text-xs ' + getMarginColor(margin)} variant="outline">
                        {margin}% margin
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  {visibleColumns.map((column) => (
                    <TableHead
                      key={column.id}
                      className={column.align === 'right' ? 'text-right' : ''}
                    >
                      {column.name}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayedEmployees.map((employee) => (
                  <TableRow
                    key={employee.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleEmployeeClick(employee)}
                  >
                    {visibleColumns.map((column) => (
                      <TableCell
                        key={column.id}
                        className={column.align === 'right' ? 'text-right' : ''}
                      >
                        {renderTableCell(employee, column.id)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )
      ) : !loading && !error ? (
        <div className="text-center py-12">
          <p className="text-gray-500">No resources found matching your criteria.</p>
          <p className="text-sm text-gray-400 mt-2">Try adjusting your search or filters.</p>
        </div>
      ) : null}

      {filteredEmployees.length > 0 && (
        <PaginationControls
          pagination={pagination}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          showPageSizeSelector={true}
        />
      )}

      <RateDetailSheet
        employee={selectedEmployee}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        navigate={navigate}
      />

      <ColumnConfiguratorDrawer
        open={columnConfigOpen}
        onOpenChange={setColumnConfigOpen}
        columns={columns}
        onSave={updateColumns}
        defaultColumns={DEFAULT_RATES_COLUMNS}
        pageKey="roles-rates"
      />
    </div>
  );
}
